import { MapGeometry } from "graphql/geospatial/queries";
import { useMapOrders } from "hooks/useMapOrders";
import { useCallback, useEffect, useRef, useState } from "react";
import { useOrdersContext } from "views/index/ordersContext";
import { Location, LocationKind } from 'types/geospatial';
import { OlMapRefType } from "components/OlMap";
import { PropertyMapType, RelatedOrder } from "./types";
import { useLocations } from "./hooks/useLocations";
import useAlert from "utils/alert";

export const useMapTab = () => {
  const showAlert = useAlert();
  const {
    agencySelectedId,
    filter,
    handleSelectRow
  } = useOrdersContext()!;

  const [selectedGeometryZone, setSelectedGeometryZone] = useState<PropertyMapType | null>(null);

  const [mapGeometries, setMapGeometries] = useState<MapGeometry[]>([]);
  const [propertySearchQuery, setPropertySearchQuery] = useState<string>("");
  const relatedOrdersRef = useRef<RelatedOrder[]>([]);
  const [relatedOrders, setRelatedorders] = useState<RelatedOrder[]>([]);
  const olMapRef = useRef<OlMapRefType>(null);

  const handleSelectGeometryZone = useCallback((zone: Location | null) => {
    const geometry = zone ? {
      geo: zone.geometry,
      _id: zone._id,
      address: zone.name
    } : null;
    setSelectedGeometryZone(geometry);
  }, []);

  const handleNoResultFound = () => {
    showAlert("No results found", "warning");
  };

  const {
    locations,
    selectedLocations,
    locationDataState,
    setSelectedLocations,
    setZctaQuery,
    zctaQuery
  } = useLocations({ onSelectGeometry: handleSelectGeometryZone, onNoResultFound: handleNoResultFound });

  useMapOrders({
    agencyId: agencySelectedId,
    status: filter,
    geometryId: selectedGeometryZone?._id ?? "",
    queryConfig: {
      onSuccess: (data) => {
        relatedOrdersRef.current = data.mapOrders;
        setMapGeometries(data.mapOrders);
        setRelatedorders(data.mapOrders);
      }
    }
  });

  const mapProperties: PropertyMapType[] =
		(selectedGeometryZone !== null ? [selectedGeometryZone!] : [])
		  .concat(
		    mapGeometries.map(({
		      address,
		      geometry
		    }) => ({
		      _id: address,
		      address,
		      geo: geometry
		    }))
		  );

  const setDefaultState = useCallback((state: Location) => {
    setSelectedLocations((prevValue) => {
      const prevSelectedLocations = { ...prevValue };
      prevSelectedLocations.state = state;

      return prevSelectedLocations;
    });
    handleSelectGeometryZone(state);
  }, [handleSelectGeometryZone, setSelectedLocations]);

  const handleSearchProperty = (query: string) => {
    setPropertySearchQuery(query);

    const rOrderList: RelatedOrder[] = [];
    relatedOrdersRef.current.forEach((order) => {
      let relatedOrder: RelatedOrder | null = null;
      if (order.address.toLowerCase().includes(query.toLowerCase())) {
        relatedOrder = { ...order };
      }

      const filteredOrders = order.orders.filter(({ order_id }) => order_id.includes(query));
      if (filteredOrders.length) {
        relatedOrder = {
          ...order,
          orders: filteredOrders
        };
      }

      if (relatedOrder) { rOrderList.push(relatedOrder); }
    });

    setRelatedorders(rOrderList);
  };

  const handleSelectAddress = (address: string) => {
    handleSearchProperty(address);
  };

  const handleClearFilters = useCallback(() => {
    setSelectedLocations({});
    setSelectedGeometryZone(null);
    setPropertySearchQuery("");
    setZctaQuery("");

    const texasState = locations.state.find(({ name }) => name === "Texas");
    if (texasState) {
      setDefaultState(texasState);
      setRelatedorders(relatedOrdersRef.current);
    }
  }, [locations, setDefaultState, setSelectedLocations, setZctaQuery]);

  const handleSelectLocation =
		useCallback((locationId: string, kind: LocationKind) => {
		  let geometry: Location | null = null;

		  handleClearFilters();

		  if (kind in locations) {
		    if (locations[kind].length) { geometry = locations[kind].find(({ _id }) => _id === locationId) ?? null; }

		    setSelectedLocations((prevValue) => {
		      const prevSelectedLocations = { ...prevValue };
		      prevSelectedLocations[kind] = geometry;

		      return prevSelectedLocations;
		    });
		  }

		  handleSelectGeometryZone(geometry);
		}, [locations, handleClearFilters, setSelectedLocations, handleSelectGeometryZone]);

  const handleSearchZcta = (query: string) => {
    handleClearFilters();
    setZctaQuery(query);
  };

  useEffect(() => {
    if (olMapRef.current?.selectFeatureById && selectedGeometryZone) {
      olMapRef.current.selectFeatureById(selectedGeometryZone._id);
    }
  }, [selectedGeometryZone]);

  return {
    locationDataState,
    selectedLocations,
    locations,
    relatedOrders,
    propertySearchQuery,
    handleSearchProperty,
    olMapRef,
    mapProperties,
    handleSelectAddress,
    handleSelectRow,
    handleSelectLocation,
    handleClearFilters,
    handleSearchZcta,
    zctaQuery
  };
};